import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Business as BusinessIcon,
  ChevronLeft,
  Dashboard as DashboardIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon
} from '@mui/icons-material';
import { ThemeToggle } from '../../components/ui/theme-toggle';
import { ServerStatus } from '../../components/ui/server-status';
import { PLATFORM_BRAND_COLOR } from '../../utils/colorUtils';

const drawerWidthOpen = 240;
const drawerWidthClosed = 65;

const readJson = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || 'null');
  } catch {
    return null;
  }
};

const GroupLayout = ({ children, onGroupLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [logoutMenuAnchor, setLogoutMenuAnchor] = useState(null);

  const groupUser = readJson('groupUser');
  const groupData = readJson('groupData');
  const platformAccentColor = theme.palette.mode === 'dark' ? '#5794F2' : PLATFORM_BRAND_COLOR;

  React.useEffect(() => {
    setDrawerOpen(!isMobile);
  }, [isMobile]);

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/group/dashboard' },
    { text: 'Typing Centers', icon: <BusinessIcon />, path: '/group/centers' }
  ];

  const getPageTitle = () => {
    if (location.pathname === '/group/dashboard') return 'Dashboard';
    if (location.pathname === '/group/centers') return 'Typing Centers';
    return 'Owner Portal';
  };

  const handleLogout = () => {
    setLogoutMenuAnchor(null);
    if (onGroupLogout) onGroupLogout();
    localStorage.removeItem('groupToken');
    localStorage.removeItem('groupUser');
    localStorage.removeItem('groupData');
    localStorage.removeItem('groupAuthenticated');
    localStorage.removeItem('actingAsGroupOwner');
    navigate('/group/login');
  };

  const avatarLabel = (groupUser?.fullName || groupUser?.username || 'O').charAt(0).toUpperCase();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? drawerOpen : true}
        onClose={() => isMobile && setDrawerOpen(false)}
        sx={{
          width: isMobile ? drawerWidthOpen : (drawerOpen ? drawerWidthOpen : drawerWidthClosed),
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: isMobile ? drawerWidthOpen : (drawerOpen ? drawerWidthOpen : drawerWidthClosed),
            boxSizing: 'border-box',
            backgroundColor: theme.palette.mode === 'dark' ? '#0d1117' : PLATFORM_BRAND_COLOR,
            color: 'white',
            display: 'flex',
            flexDirection: 'column',
            '& .MuiListItemText-primary, & .MuiListItemIcon-root, & .MuiSvgIcon-root': {
              color: 'white !important'
            },
            borderTopRightRadius: isMobile ? 0 : 15,
            borderBottomRightRadius: isMobile ? 0 : 15,
            transition: 'width 0.2s',
            overflowX: 'hidden'
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: (drawerOpen || isMobile) ? 'row' : 'column',
            alignItems: 'center',
            gap: 1.5,
            px: (drawerOpen || isMobile) ? 2 : 1,
            pt: 2,
            pb: 1.5,
            minHeight: 64
          }}
        >
          <Avatar
            sx={{
              width: 44,
              height: 44,
              bgcolor: 'rgba(255,255,255,0.2)',
              border: '2px solid rgba(255,255,255,0.2)',
              fontWeight: 600,
              fontSize: '1rem',
              color: 'white',
              flexShrink: 0,
              '& img': { objectFit: 'contain' }
            }}
          >
            {avatarLabel}
          </Avatar>
          {(drawerOpen || isMobile) && (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, color: 'white', fontSize: '0.9rem', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {groupUser?.fullName || groupUser?.username || 'Owner'}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {groupData?.name || 'Typing Center Group'}
              </Typography>
            </Box>
          )}
          {!isMobile && (
            <IconButton onClick={() => setDrawerOpen(!drawerOpen)} sx={{ color: 'white', p: 0.5, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}>
              <ChevronLeft sx={{ transform: drawerOpen ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.2s' }} />
            </IconButton>
          )}
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

        <Box sx={{ pt: 1, px: 1 }}>
          <List disablePadding>
            {menuItems.map((item) => {
              const active = location.pathname === item.path;
              return (
                <ListItem key={item.text} disablePadding sx={{ display: 'block', mb: 0.3 }}>
                  <ListItemButton
                    onClick={() => {
                      navigate(item.path);
                      if (isMobile) setDrawerOpen(false);
                    }}
                    sx={{
                      minHeight: 44,
                      justifyContent: (drawerOpen || isMobile) ? 'initial' : 'center',
                      px: 1.5,
                      py: 0.8,
                      borderRadius: '8px',
                      backgroundColor: active ? 'rgba(255,255,255,0.15)' : 'transparent',
                      transition: 'all 0.15s ease',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 0, mr: (drawerOpen || isMobile) ? 1.5 : 'auto', justifyContent: 'center', color: 'white', opacity: 0.9, '& .MuiSvgIcon-root': { fontSize: '1.25rem' } }}>
                      {item.icon}
                    </ListItemIcon>
                    {(drawerOpen || isMobile) && (
                      <ListItemText
                        primary={item.text}
                        sx={{
                          '& .MuiListItemText-primary': {
                            color: 'white',
                            fontWeight: active ? 600 : 400,
                            fontSize: '0.875rem'
                          }
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>

        <Box sx={{ mt: 'auto', p: 1.5, textAlign: 'center', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
            <img src="/thaklis-logo.png" alt="Thaklis" style={{ width: 20, height: 20, objectFit: 'contain' }} />
            {(drawerOpen || isMobile) && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.2, letterSpacing: '0.05em' }}>
                  Powered by
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'rgba(255,255,255,0.6)', lineHeight: 1.2 }}>
                  Thaklis
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <AppBar
          position="fixed"
          sx={{
            width: isMobile ? '100%' : `calc(100% - ${drawerOpen ? drawerWidthOpen : drawerWidthClosed}px)`,
            ml: isMobile ? 0 : `${drawerOpen ? drawerWidthOpen : drawerWidthClosed}px`,
            backgroundColor: 'background.paper',
            color: 'text.primary',
            boxShadow: 1,
            transition: 'width 0.2s, margin 0.2s'
          }}
        >
          <Toolbar>
            {isMobile && (
              <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={() => setDrawerOpen(true)} sx={{ mr: 2, color: platformAccentColor }}>
                <MenuIcon />
              </IconButton>
            )}
            <Typography variant="h6" component="div" noWrap sx={{ flexGrow: 0, mr: 2, fontWeight: 600, fontSize: { xs: '0.85rem', sm: '1rem' }, maxWidth: { xs: 150, sm: 'none' } }}>
              {getPageTitle()}
            </Typography>
            {!isMobile && (
              <Chip
                label={groupData?.name || 'Typing Center Group'}
                variant="outlined"
                size="small"
                sx={{
                  mr: 1,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  color: platformAccentColor,
                  borderColor: platformAccentColor
                }}
              />
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ServerStatus />
              <ThemeToggle />
              <Tooltip title="Logout" arrow>
                <IconButton
                  size="small"
                  onClick={(e) => setLogoutMenuAnchor(e.currentTarget)}
                  sx={{
                    color: '#64748b',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.04)',
                      color: '#ef4444'
                    }
                  }}
                >
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Menu anchorEl={logoutMenuAnchor} open={Boolean(logoutMenuAnchor)} onClose={() => setLogoutMenuAnchor(null)}>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Box sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2, md: 3 }, mt: 8, backgroundColor: 'background.default', minHeight: 'calc(100vh - 64px)', overflow: 'hidden' }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default GroupLayout;
